import { useMinistries } from '@/features/master-table/hooks';
import {
  ActivityFlowSection,
  LedgerPage,
  PaymentSection,
  RelatedMaterialSection,
  SsBasicInfoSection,
  SsBudgetAndExecutionSection,
  SsInspectionEvaluationSection,
} from '@/features/mold/components';
import {
  PAPER_FILE_SIZE,
  getConcatenatedBusinessManagerName,
} from '@/features/mold/utils';
import {
  useActivityTreeOptions,
  useNestedConsecutiveIndicators,
} from '@/features/project/_hooks/activity';
import { useBusinessManagers } from '@/features/project/_hooks/business-managers';
import { useConsecutiveFiscalYears } from '@/features/project/_hooks/consecutive';
import { useProjectHasRelatedOrganizations } from '@/features/project/_hooks/organization';
import { useContractorPayments } from '@/features/project/_hooks/payment';
import { usePaymentGraph } from '@/features/project/_hooks/payment-graph';
import { usePaymentGroups } from '@/features/project/_hooks/payment-group';
import { useProject } from '@/features/project/_hooks/project';
import { useRelatedMaterials } from '@/features/project/_hooks/related-material';
import { useSegmentExpenditureAndExecution } from '@/features/project/_hooks/segment-expenditure-and-execution';
import { useOrganization } from '@/features/tenant-manager/hooks';
import { useId } from '@/hooks';
import { uniq } from 'lodash-es';

export const SsProjectLedgerPage = () => {
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const projectOrganizations = useProjectHasRelatedOrganizations({
    projectId,
    params: {
      showMigrated: true,
      historySavedAt: project.data?.historySavedAt,
    },
    enabled: !!projectId,
  });
  const independentAdministrativeAgency = useOrganization({
    id: project.data?.independentAdministrativeAgencyId ?? '',
  });

  const ministries = useMinistries();
  const concatenatedBusinessManagerName = useBusinessManagers({
    projectId,
    select: getConcatenatedBusinessManagerName,
  });
  const activityTreeOption = useActivityTreeOptions(projectId);
  const routeIndicatorIds = uniq(
    activityTreeOption.selectOptions.flatMap((option) =>
      option.routeIndicators.flatMap((v) => v.indicator.id),
    ),
  );
  const relatedMaterials = useRelatedMaterials(projectId);
  const paymentGroups = usePaymentGroups({ projectId });
  const paymentGraph = usePaymentGraph(projectId);
  const payments = useContractorPayments({ projectId });
  const segmentExpenditureAndExecution = useSegmentExpenditureAndExecution({
    projectId,
  });
  const nestedConsecutiveIndicators =
    useNestedConsecutiveIndicators(routeIndicatorIds);

  const consecutiveFiscalYears = useConsecutiveFiscalYears({ id: projectId });

  const isLoading =
    project.isLoading ||
    segmentExpenditureAndExecution.isLoading ||
    ministries.isLoading ||
    projectOrganizations.isLoading ||
    concatenatedBusinessManagerName.isLoading ||
    relatedMaterials.isLoading ||
    paymentGroups.isLoading;

  return (
    <LedgerPage width={PAPER_FILE_SIZE.A4.height} isLoading={isLoading}>
      <SsBasicInfoSection
        project={project.data}
        ministries={ministries.data}
        organizations={projectOrganizations.data}
        concatenatedBusinessManagerName={concatenatedBusinessManagerName.data}
        independentAdministrativeAgency={independentAdministrativeAgency.data}
      />
      <SsBudgetAndExecutionSection
        project={project.data}
        segmentExpenditureAndExecution={segmentExpenditureAndExecution.data}
        consecutiveFiscalYears={consecutiveFiscalYears.data}
      />
      <ActivityFlowSection
        project={project.data}
        nestedConsecutiveIndicators={nestedConsecutiveIndicators.data}
        tree={activityTreeOption.tree.data}
        selectOptions={activityTreeOption.selectOptions}
      />
      <SsInspectionEvaluationSection project={project.data} />
      <PaymentSection
        project={project.data}
        paymentGraph={paymentGraph.data}
        paymentGroups={paymentGroups.data}
        payments={payments.data}
      />
      <RelatedMaterialSection relatedMaterials={relatedMaterials.data} />
    </LedgerPage>
  );
};
